import React, { useEffect, useMemo, useState } from "react";
import "./AdminFeedbackPanel.css";
import { BASE_URL, ensureArray, getApiHeaders } from "./config";
import { getAuthToken } from "./auth/authUtils";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
};

const AdminFeedbackPanel = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const fetchFeedbacks = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getAuthToken();
      const response = await fetch(`${BASE_URL}/api/bot/ui/feedback/admin/all`, {
        headers: getApiHeaders({
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Unable to load feedback: ${response.status}`);
      }

      const data = await response.json();
      setFeedbacks(ensureArray(data));
    } catch (err) {
      console.error("Unable to load feedback", err);
      setError(err.message || "Unable to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const filteredFeedbacks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return feedbacks;

    return feedbacks.filter((feedback) => {
      return [
        feedback.email,
        feedback.message,
        feedback.sourcePage,
        feedback.createdDate,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [feedbacks, query]);

  const uniqueSources = useMemo(
    () => new Set(feedbacks.map((feedback) => feedback.sourcePage).filter(Boolean)).size,
    [feedbacks]
  );

  return (
    <section className="admin-feedback-panel">
      <div className="admin-feedback-header">
        <div>
          <span className="admin-feedback-eyebrow">Visitor Feedback</span>
          <h2>Feedback Inbox</h2>
          <p>Review landing-page feedback, product ideas, and issue reports submitted by visitors.</p>
        </div>
        <button className="admin-feedback-refresh" onClick={fetchFeedbacks} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="admin-feedback-stats">
        <div>
          <span>Total Feedback</span>
          <strong>{feedbacks.length}</strong>
        </div>
        <div>
          <span>Visible Results</span>
          <strong>{filteredFeedbacks.length}</strong>
        </div>
        <div>
          <span>Source Pages</span>
          <strong>{uniqueSources}</strong>
        </div>
      </div>

      <div className="admin-feedback-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search email, message, source page, or date..."
        />
      </div>

      {error && <div className="admin-feedback-error">{error}</div>}

      {!error && loading ? (
        <div className="admin-feedback-empty">Loading feedback...</div>
      ) : !error && filteredFeedbacks.length === 0 ? (
        <div className="admin-feedback-empty">No feedback found.</div>
      ) : (
        <div className="admin-feedback-table-wrap">
          <table className="admin-feedback-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Email</th>
                <th>Source</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeedbacks.map((feedback) => (
                <tr key={feedback.id}>
                  <td>{formatDateTime(feedback.createdDate)}</td>
                  <td>{feedback.email || "Anonymous"}</td>
                  <td>
                    <span className="admin-feedback-source">
                      {feedback.sourcePage || "Unknown"}
                    </span>
                  </td>
                  <td className="admin-feedback-message">{feedback.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminFeedbackPanel;
